import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  LayoutDashboard, UserPlus, Users, FolderKanban, Hammer, HardHat, Package, Megaphone, Building2,
  LifeBuoy, LogOut, Menu, X, Lock, ChevronRight, Coins, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { PartnerProvider, usePartner, OPEN_BEFORE_VALIDATION } from "./PartnerContext";
import { Locked, Btn } from "./ui";
import { useAuth } from "@/context/AuthContext";

export const NAV = [
  { to: "/partenaire", label: "Tableau de bord", icon: LayoutDashboard, end: true, key: "dashboard" },
  { to: "/partenaire/agenda", label: "Agenda", icon: CalendarDays, ownerOnly: true },
  { to: "/partenaire/prospects", label: "Prospects", icon: UserPlus, permissions: ["prospects.view_all", "prospects.view_assigned"] },
  { to: "/partenaire/clients", label: "Clients", icon: Users, permissions: ["clients.view_all", "clients.view_assigned"] },
  { to: "/partenaire/projets", label: "Projets", icon: FolderKanban, permissions: ["projects.view_all", "projects.view_assigned"] },
  { to: "/partenaire/petits-travaux", label: "Petits travaux", icon: Hammer, ownerOnly: true },
  { to: "/partenaire/professionnels", label: "Professionnels", icon: HardHat, permissions: ["site.view"] },
  { to: "/partenaire/services", label: "Services CozyHome", icon: Package, permissions: ["central_orders.view", "central_orders.create"] },
  { to: "/partenaire/communication", label: "Communication & Boutique", icon: Megaphone, ownerOnly: true },
  { to: "/partenaire/commissions", label: "Commissions & Avantages", icon: Coins, permissions: ["finance.view_global_revenue"] },
  { to: "/partenaire/entreprise", label: "Mon entreprise", icon: Building2 },
];

const isOpenPath = (pathname) => OPEN_BEFORE_VALIDATION.includes(pathname);

const NavItems = ({ onNavigate, compact = false, suffix = "" }) => {
  const { validated, internalRole, canAny, pendingProspects, collaborationsIncoming, refusedDocumentsCount } = usePartner();
  const items = NAV.filter((item) => !item.ownerOnly || internalRole === "OWNER").filter((item) => !item.permissions || canAny(item.permissions));
  const badgeFor = (key) => {
    if (key === "prospects") return (pendingProspects?.length || 0) + (collaborationsIncoming?.length || 0);
    if (key === "entreprise") return refusedDocumentsCount || 0;
    return 0;
  };
  return (
    <nav className="flex flex-col gap-0.5" aria-label="Navigation Espace Partenaire">
      {items.map((item) => {
        const locked = !validated && !isOpenPath(item.to);
        const navKey = item.key || item.to.split("/").pop();
        const badge = badgeFor(navKey);
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`partner-nav-${navKey}${suffix}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                isActive ? "bg-white/12 text-white" : "text-white/65 hover:bg-white/8 hover:text-white"
              } ${compact ? "py-3" : ""}`
            }
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1 truncate">{item.label}</span>
            {badge > 0 && (
              <span data-testid={`partner-nav-badge-${navKey}${suffix}`} className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-cognac px-1.5 text-[10px] font-bold text-white">
                {badge}
              </span>
            )}
            {locked && <Lock className="h-3.5 w-3.5 text-white/45" aria-label="Verrouillé" />}
          </NavLink>
        );
      })}
    </nav>
  );
};

const SidebarFooter = ({ onNavigate, suffix = "" }) => {
  const { partner, currentUser } = usePartner();
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="mt-auto flex flex-col gap-2 pt-4">
      <button
        data-testid={`partner-nav-aide${suffix}`}
        onClick={() => toast.info("L'aide en ligne sera disponible dans une prochaine phase.")}
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-white/65 transition-colors hover:bg-white/8 hover:text-white"
      >
        <LifeBuoy className="h-[18px] w-[18px]" /> Aide
      </button>
      <Link
        to="/partenaire/entreprise"
        onClick={onNavigate}
        data-testid={`partner-nav-profil${suffix}`}
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-white/8"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cognac font-display text-base font-semibold text-white">
          {currentUser.firstName[0]}{currentUser.lastName[0]}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-white">{currentUser.firstName} {currentUser.lastName}</span>
          <span className="block truncate text-[11px] text-white/55">{partner.company}</span>
        </span>
      </Link>
      <button
        data-testid={`partner-nav-deconnexion${suffix}`}
        onClick={() => {
          sessionStorage.removeItem(PENDING_MODAL_SESSION_KEY);
          logout().then(() => navigate("/acces-phase3"));
        }}
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-white/65 transition-colors hover:bg-white/8 hover:text-white"
      >
        <LogOut className="h-[18px] w-[18px]" /> Déconnexion
      </button>
    </div>
  );
};

const Brand = ({ suffix = "" }) => (
  <Link to="/partenaire" data-testid={`partner-brand${suffix}`} className="flex items-center gap-3">
    <span className="flex h-10 shrink-0 items-center justify-center rounded-xl bg-cream px-2.5">
      <Logo className="h-7 w-auto" />
    </span>
    <span>
      <span className="block font-display text-lg font-semibold leading-none text-white">CozyHome</span>
      <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.24em] text-cognac-light">Espace Partenaire</span>
    </span>
  </Link>
);

const PENDING_MODAL_SESSION_KEY = "cozyhome_pending_modal_shown";

const PendingProspectsModal = () => {
  const { pendingProspects, acceptTerritorialProspect, refuseTerritorialProspect } = usePartner();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(PENDING_MODAL_SESSION_KEY) === "1");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (pendingProspects.length > 0) sessionStorage.setItem(PENDING_MODAL_SESSION_KEY, "1");
  }, [pendingProspects.length]);

  if (dismissed || pendingProspects.length === 0) return null;

  const act = async (id, action) => {
    setBusy(id);
    await (action === "accept" ? acceptTerritorialProspect(id) : refuseTerritorialProspect(id));
    setBusy(null);
  };

  return (
    <div data-testid="pending-prospects-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-navy/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <p className="text-xs font-bold uppercase text-petrol">
          {pendingProspects.length > 1 ? `Nouveaux prospects CozyHome` : "Nouveau prospect CozyHome"}
        </p>
        <h2 className="mt-1 text-lg font-bold text-navy">
          {pendingProspects.length > 1
            ? `Vous avez ${pendingProspects.length} nouvelles demandes de prise en charge`
            : "Une nouvelle demande vous a été proposée"}
        </h2>
        <div className="mt-4 max-h-[50vh] space-y-3 overflow-y-auto">
          {pendingProspects.map((p) => (
            <div key={p.id} data-testid={`pending-modal-item-${p.id}`} className="rounded-xl border border-[#E3E8E9] p-4">
              <p className="text-sm font-bold text-navy">{p.name} — {p.city} ({p.project})</p>
              <p className="mt-1 text-xs text-stone-500">Budget : {p.budget || "non précisé"} · Reçu le {p.createdAt}</p>
              {p.slaDeadline && (
                <p className="mt-1 text-xs font-semibold text-amber-700">
                  Délai de réponse jusqu'au {new Date(p.slaDeadline).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <Btn testid={`pending-modal-accept-${p.id}`} disabled={busy === p.id} onClick={() => act(p.id, "accept")}>Accepter</Btn>
                <Btn variant="ghost" testid={`pending-modal-refuse-${p.id}`} disabled={busy === p.id} onClick={() => act(p.id, "refuse")}>Refuser</Btn>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="pending-modal-close" onClick={() => setDismissed(true)} className="mt-4 text-xs font-semibold text-stone-400 hover:text-stone-600">
          Fermer (les demandes restent visibles dans Prospects)
        </button>
      </div>
    </div>
  );
};

const Shell = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { validated, internalRole, canAny } = usePartner();
  const allowedNav = NAV.filter((item) => !item.ownerOnly || internalRole === "OWNER").filter((item) => !item.permissions || canAny(item.permissions));

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [location.pathname]);

  const locked = !validated && !isOpenPath(location.pathname) && !location.pathname.startsWith("/partenaire/projets/");
  const lockedDetail = !validated && location.pathname.startsWith("/partenaire/projets/");
  const current = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));

  return (
    <div data-testid="partner-space" className="min-h-screen bg-petrol-bg font-sans text-navy">
      {/* Sidebar desktop */}
      <aside
        data-testid="partner-sidebar"
        className="fixed inset-y-0 left-0 z-40 hidden w-[19rem] flex-col bg-petrol px-4 py-6 lg:flex"
      >
        <Brand />
        <div className="mt-8 flex-1 overflow-y-auto">
          <NavItems />
        </div>
        <SidebarFooter />
      </aside>

      {/* Barre mobile */}
      <header
        data-testid="partner-mobile-header"
        className="sticky top-0 z-40 flex h-16 items-center justify-between bg-petrol px-4 lg:hidden"
      >
        <Brand suffix="-mobile" />
        <button
          data-testid="partner-mobile-menu-open"
          onClick={() => setOpen(true)}
          aria-label="Ouvrir la navigation"
          className="rounded-full border border-white/25 p-2.5 text-white"
        >
          <Menu className="h-5 w-5" />
        </button>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-navy/40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              data-testid="partner-mobile-menu"
              className="fixed inset-y-0 left-0 z-[70] flex w-[86%] max-w-xs flex-col overflow-y-auto bg-petrol px-4 py-5 lg:hidden"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-between">
                <Brand suffix="-drawer" />
                <button
                  data-testid="partner-mobile-menu-close"
                  onClick={() => setOpen(false)}
                  aria-label="Fermer la navigation"
                  className="rounded-full border border-white/25 p-2 text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-6 flex-1">
                <NavItems compact suffix="-mobile" onNavigate={() => setOpen(false)} />
              </div>
              <SidebarFooter suffix="-mobile" onNavigate={() => setOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Contenu */}
      <main className="lg:pl-[19rem]">
        <div className="mx-auto max-w-[1400px] px-4 pb-28 pt-6 sm:px-6 lg:px-10 lg:pb-16 lg:pt-8">
          <div data-testid="partner-breadcrumb" className="mb-5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">
            <span className="text-petrol">Espace Partenaire</span>
            {current && (
              <>
                <ChevronRight className="h-3 w-3" />
                <span>{current.label}</span>
              </>
            )}
            {!validated && (
              <span data-testid="partner-onboarding-pill" className="ml-auto rounded-full bg-cognac/10 px-2.5 py-1 normal-case tracking-normal text-cognac-dark">
                Dossier en cours d'intégration
              </span>
            )}
          </div>
          {locked || lockedDetail ? <Locked title={current?.label || "Projet"} /> : <Outlet />}
        </div>
      </main>

      {/* Barre d'accès rapide mobile */}
      <nav
        data-testid="partner-mobile-tabbar"
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-[#E3E8E9] bg-white/95 backdrop-blur lg:hidden"
        aria-label="Accès rapide"
      >
        {allowedNav.slice(0, 4).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`partner-tab-${item.key || item.to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2.5 text-[10px] font-bold ${isActive ? "text-petrol" : "text-stone-400"}`
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label.split(" ")[0]}
          </NavLink>
        ))}
        <button
          data-testid="partner-tab-menu"
          onClick={() => setOpen(true)}
          className="flex flex-col items-center gap-1 py-2.5 text-[10px] font-bold text-stone-400"
        >
          <Menu className="h-5 w-5" />
          Menu
        </button>
      </nav>
      <PendingProspectsModal />
    </div>
  );
};

export default function PartnerLayout() {
  return (
    <PartnerProvider>
      <Shell />
    </PartnerProvider>
  );
}
